import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { CampaignLink } from '@/types/referral';
import { useToast } from '@/hooks/use-toast';

interface UseCampaignLinksReturn {
  campaignLinks: CampaignLink[];
  loading: boolean;
  error: string | null;
  createCampaignLink: (campaignName: string, referralUrl: string) => Promise<CampaignLink | null>;
  deleteCampaignLink: (id: string) => Promise<boolean>;
  refreshCampaignLinks: () => Promise<void>;
}

export function useCampaignLinks(userId?: string): UseCampaignLinksReturn {
  const [campaignLinks, setCampaignLinks] = useState<CampaignLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchCampaignLinks = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setError(null);

      // Get campaign links from localStorage
      const storedCampaigns = JSON.parse(localStorage.getItem(`campaign_links_${userId}`) || '[]');

      // Get the user's referral code data to populate visit counts from campaign_visits
      const { data: referralCodeData, error: referralError } = await supabase
        .from('referral_codes')
        .select('campaign_visits')
        .eq('user_id', userId)
        .single();

      if (referralError && referralError.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.warn('Could not fetch referral code data:', referralError);
      }

      // Update visit counts from the referral system
      const campaignVisits = referralCodeData?.campaign_visits || {};
      const updatedCampaigns = storedCampaigns.map((campaign: CampaignLink) => ({
        ...campaign,
        visit_count: campaignVisits[campaign.campaign_slug] || 0
      }));

      setCampaignLinks(updatedCampaigns);
    } catch (err) {
      console.error('Error fetching campaign links:', err);
      setError(err instanceof Error ? err.message : 'Failed to load campaign links');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const createCampaignLink = async (campaignName: string, referralUrl: string): Promise<CampaignLink | null> => {
    if (!userId) {
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive"
      });
      return null;
    }

    try {
      // Create a URL-safe slug from the campaign name
      const campaignSlug = campaignName.trim().toLowerCase().replace(/[^a-z0-9]/g, '-');

      // Create the tracked URL with the campaign parameter
      const separator = referralUrl.includes('?') ? '&' : '?';
      const trackedUrl = `${referralUrl}${separator}campaign=${campaignSlug}`;

      // Create a mock campaign link object that matches the expected interface
      // This integrates with the existing referral system rather than creating a separate table
      const mockCampaignLink: CampaignLink = {
        id: `campaign_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        user_id: userId,
        campaign_name: campaignName.trim(),
        campaign_slug: campaignSlug,
        referral_url: trackedUrl,
        visit_count: 0,
        signup_count: 0,
        conversion_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Store the campaign link in localStorage for persistence
      const existingCampaigns = JSON.parse(localStorage.getItem(`campaign_links_${userId}`) || '[]');

      // Check for duplicate campaign names
      if (existingCampaigns.some((c: CampaignLink) => c.campaign_slug === campaignSlug)) {
        toast({
          title: "Campaign already exists",
          description: "A campaign with this name already exists. Please choose a different name.",
          variant: "destructive"
        });
        return null;
      }

      const updatedCampaigns = [mockCampaignLink, ...existingCampaigns];
      localStorage.setItem(`campaign_links_${userId}`, JSON.stringify(updatedCampaigns));

      // Add the new campaign link to the state
      setCampaignLinks(prev => [mockCampaignLink, ...prev]);

      toast({
        title: "Campaign link created",
        description: `Campaign "${campaignName}" has been created successfully. This will track visits through the existing referral system.`
      });

      return mockCampaignLink;
    } catch (err) {
      console.error('Error creating campaign link:', err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : 'Failed to create campaign link',
        variant: "destructive"
      });
      return null;
    }
  };

  const deleteCampaignLink = async (id: string): Promise<boolean> => {
    if (!userId) return false;

    try {
      // Remove from localStorage
      const existingCampaigns = JSON.parse(localStorage.getItem(`campaign_links_${userId}`) || '[]');
      const updatedCampaigns = existingCampaigns.filter((campaign: CampaignLink) => campaign.id !== id);
      localStorage.setItem(`campaign_links_${userId}`, JSON.stringify(updatedCampaigns));

      // Remove the campaign link from the state
      setCampaignLinks(prev => prev.filter(link => link.id !== id));

      toast({
        title: "Campaign link deleted",
        description: "The campaign link has been deleted successfully."
      });

      return true;
    } catch (err) {
      console.error('Error deleting campaign link:', err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : 'Failed to delete campaign link',
        variant: "destructive"
      });
      return false;
    }
  };

  const refreshCampaignLinks = async () => {
    setLoading(true);
    await fetchCampaignLinks();
  };

  useEffect(() => {
    fetchCampaignLinks();
  }, [userId, fetchCampaignLinks]);

  return {
    campaignLinks,
    loading,
    error,
    createCampaignLink,
    deleteCampaignLink,
    refreshCampaignLinks
  };
}
